import React, { useRef } from 'react';
import { Send } from 'lucide-react';
import { cn } from '../../utils';
import { usePrimaryFill } from '../../hooks/usePrimaryFill';
import { useT } from '../../i18n';
import styles from './ChatInput.module.css';

export interface ChatInputProps {
  value: string;
  /** Placeholder text (defaults to the localized help prompt) */
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  textColor?: string;
  primaryColor?: string;
  /** Fill applied on hover of the send button */
  primaryHoverColor?: string;
  useGradient?: boolean;
  onInputChange?: (value: string) => void;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  className?: string;
  absolute?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

export function ChatInput({
  value,
  placeholder,
  disabled = false,
  isLoading = false,
  textColor = '#1e293b',
  primaryColor,
  primaryHoverColor,
  useGradient = false,
  onInputChange,
  onSubmit,
  className,
  absolute = true,
  inputRef,
}: ChatInputProps) {
  const t = useT();
  const fallbackInputRef = useRef<HTMLInputElement>(null);
  const actualInputRef = inputRef || fallbackInputRef;

  const { style: sendFillStyle, onMouseEnter, onMouseLeave } = usePrimaryFill(
    primaryColor,
    primaryHoverColor,
    useGradient,
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!disabled && !isLoading && value.trim().length > 0) {
      onSubmit?.(e);
    }
  };

  const showSendButton = value.length > 0;

  return (
    <div className={cn(styles.outer, absolute && styles.absolute, className)}>
      <div className={styles.container}>
        <div className={styles.inputWrapper}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputContainer}>
              <input
                ref={actualInputRef as React.RefObject<HTMLInputElement>}
                className={styles.input}
                style={{ color: textColor }}
                disabled={disabled || isLoading}
                type="text"
                placeholder={isLoading ? t('input.waitAMoment') : placeholder || t('input.placeholder')}
                value={value}
                onChange={(e) => onInputChange?.(e.target.value)}
                aria-label={t('input.messageInput')}
                aria-busy={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={disabled || isLoading || value.length === 0}
              className={cn(styles.sendButton, !showSendButton && styles.hidden)}
              style={sendFillStyle}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              aria-label={isLoading ? t('input.sending') : t('input.send')}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className={styles.spinner} aria-hidden="true" />
              ) : (
                <Send size={20} aria-hidden="true" />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
